import { SDKFactory } from './helpers/sdk.factory';
import { Jobs } from './subcollections/queues-jobs';
import { Emails } from './subcollections/queues-emails';
import { SDK } from './helpers/sdk';
import type { SpotifyLookup } from '@sda/interfaces/queue/spotify';
import type { ShowNotificationEmail } from '@sda/interfaces/queue/shownotificationemail';
import type { BeaconNotificationEmail } from '@sda/interfaces/queue/beaconnotificationemail';
import type { AnalyticsDispatch } from '@sda/interfaces/queue/analytics';
import type { ReviewNotificationEmail } from '@sda/interfaces/queue/reviewnotificationemail';
import type { Payout } from '@sda/interfaces/queue/payout';
import type { ShowDelete } from '@sda/interfaces/queue/showdelete';
import type { Settlement } from '@sda/interfaces/queue/settlement';
import type { UserDelete } from '@sda/interfaces/queue/userdelete';
import type { NotificationEmail } from '@sda/interfaces/queue/threadnotificationemail';
import type { ProfileDelete } from '@sda/interfaces/queue/profiledelete';
import type { QRCode } from '@sda/interfaces/queue/qrcode';

/**
 * SDK Class to interact with Firestore Queues.
 */
export class Queues extends SDK<never> {
  /**
   * Returns a FirestoreBaseClass instance for the analytics/dispatch collection of the queue document
   * @return {any}
   */
  Analytics(): SDK<AnalyticsDispatch> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<AnalyticsDispatch>(clientData, Schema.AnalyticsQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the message collection of the queue document
   * @return {any}
   */
  Emails(): typeof EmailsSDK {
    const { clientData } = this.fsh;

    const EmailsSDK = new Emails(clientData);

    return EmailsSDK;
  }

  /**
   * Returns a FirestoreBaseClass instance for the payout collection of the queue document
   * @return {any}
   */
  Payouts(): SDK<Payout> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<Payout>(clientData, Schema.PayoutsQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the Jobs collection of the queue document
   */
  Jobs(): typeof JobsSDK {
    const { clientData } = this.fsh;

    const JobsSDK = new Jobs(clientData);

    return JobsSDK;
  }

  /**
   * Returns a FirestoreBaseClass instance for the ShowDeletes collection of the queue document
   */
  ShowDeletes(): SDK<ShowDelete> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<ShowDelete>(clientData, Schema.ShowDeletesQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the Settlements collection of the queue document
   */
  Settlements(): SDK<Settlement> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<Settlement>(clientData, Schema.SettlementsQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the UserDeletes collection of the queue document
   */
  UserDeletes(): SDK<UserDelete> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<UserDelete>(clientData, Schema.UserDeletesQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the ProfileDeletes collection of the queue document
   */
  ProfileDeletes(): SDK<ProfileDelete> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<ProfileDelete>(clientData, Schema.ProfileDeletesQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the BeaconNotificationEmails collection of the queue document
   */
  BeaconNotificationEmails(): SDK<BeaconNotificationEmail> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<BeaconNotificationEmail>(clientData, Schema.BeaconNotificationEmailsQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the ReviewNotificationEmails collection of the queue document
   */
  ReviewNotificationEmails(): SDK<ReviewNotificationEmail> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<ReviewNotificationEmail>(clientData, Schema.ReviewNotificationEmailsQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the ShowNotificationEmails collection of the queue document
   */
  ShowNotificationEmails(): SDK<ShowNotificationEmail> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<ShowNotificationEmail>(clientData, Schema.ShowNotificationEmailsQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the ThreadNotificationEmails collection of the queue document
   */
  ThreadNotificationEmails(): SDK<NotificationEmail> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<NotificationEmail>(clientData, Schema.ThreadNotificationEmailsQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the QRCodes collection of the queue document
   */
  QRCodes(): SDK<QRCode> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<QRCode>(clientData, Schema.QRCodesQueue);
  }

  /**
   * Returns a FirestoreBaseClass instance for the SpotifyLookups collection of the queue document
   */
  Spotify(): SDK<SpotifyLookup> {
    const { clientData, Schema } = this.fsh;

    return SDKFactory<SpotifyLookup>(clientData, Schema.SpotifyLookupsQueue);
  }
}
